import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import { apiClient } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import {
  ArrowRight,
  ShieldCheck,
  HandCoins,
  Building2,
  LineChart,
  ClipboardList,
  Briefcase,
  BadgeCheck,
  Wallet,
  PhoneCall,
  Users,
  FileSearch,
  Banknote,
  ChevronDown,
  Sparkles,
  Calculator,
  CheckCircle2,
  Quote,
  Clock,
  TrendingUp,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/8112160/pexels-photo-8112160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TIERS = [
  { min: 100_000, max: 500_000, rate: 0.01, label: "$100K – $500K", rateLabel: "1%" },
  { min: 500_000, max: 1_000_000, rate: 0.015, label: "$500K – $1M", rateLabel: "1.5%" },
  { min: 1_000_000, max: 5_000_000, rate: 0.02, label: "$1M+", rateLabel: "2%" },
];

const formatUSD = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const computeCommission = (amount) => {
  if (amount < 500_000) return amount * 0.01;
  if (amount < 1_000_000) return amount * 0.015;
  return amount * 0.02;
};

/* ───────────── Story examples ───────────── */
const STORIES = [
  {
    industry: "Manufacturing",
    referrer: "Maria",
    referrerRole: "CPA, mid-Atlantic",
    color: "blue",
    amount: 750_000,
    commission: 11_250,
    days: 17,
    headline: "A long-time client needed equipment financing — fast.",
    narrative: [
      "Maria's been the CPA for a precision metals shop for nine years. Over coffee, the owner mentioned a $750K CNC machine purchase that the bank was dragging on.",
      "Maria submitted the referral through her partner dashboard in under two minutes. Our team called the owner the same afternoon, pulled bank statements and tax returns, and placed it with an asset-based lender.",
    ],
    outcome: "Funded in 17 days. Maria earned $11,250 — wired to her ACH the day after close. She kept her client relationship intact, and her client got the machine.",
    quote: "I didn't have to learn anything new. I just made an intro and got paid.",
  },
  {
    industry: "Hospitality",
    referrer: "James",
    referrerRole: "Commercial real-estate broker",
    color: "emerald",
    amount: 1_500_000,
    commission: 30_000,
    days: 28,
    headline: "A restaurant group was expanding to a third location.",
    narrative: [
      "James was helping a restaurant group sign a third lease. They were short on the build-out and FF&E budget — about $1.5M in total capital. James had heard about becomelenders.com from another broker.",
      "He submitted the lead, we underwrote it as a mixed SBA + working-capital stack, and produced two offers within ten days. The owners picked the better-priced one and closed.",
    ],
    outcome: "Funded in 28 days. $30,000 commission to James — the largest single-deal commission he'd earned outside his real-estate work.",
    quote: "I would've otherwise watched my client struggle. This turned a side conversation into a real paycheck.",
  },
  {
    industry: "Logistics",
    referrer: "Devon",
    referrerRole: "Friend of the owner",
    color: "slate",
    amount: 250_000,
    commission: 2_500,
    days: 9,
    headline: "His college roommate needed a quick line of credit.",
    narrative: [
      "Devon isn't a finance guy. He works in software. His friend runs a 14-truck logistics company and was burning cash on fuel between AR cycles. He needed a $250K line of credit.",
      "Devon submitted the referral on his phone. We placed it with a working-capital lender who specializes in trucking.",
    ],
    outcome: "Funded in 9 days. Devon earned $2,500 for one text message and a 90-second form.",
    quote: "Easiest money I've ever made. The hardest part was typing my friend's address.",
  },
];

const StoryCard = ({ s, signupPath }) => {
  const colorMap = {
    blue:   { ring: "ring-blue-100",    accent: "text-blue-600",    chip: "bg-blue-50 text-blue-700",    line: "bg-blue-600" },
    emerald:{ ring: "ring-emerald-100", accent: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700", line: "bg-emerald-600" },
    slate:  { ring: "ring-slate-200",   accent: "text-slate-700",   chip: "bg-slate-100 text-slate-700",  line: "bg-slate-700" },
  };
  const c = colorMap[s.color];
  return (
    <article className={`group relative rounded-3xl bg-white border border-slate-200 ring-4 ${c.ring} ring-opacity-40 p-8 md:p-9 hover:shadow-xl transition-all flex flex-col`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${c.chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${c.line}`} /> {s.industry}
        </span>
        <span className="text-xs text-slate-500">Illustrative</span>
      </div>

      <h3 className="mt-5 text-xl md:text-2xl font-heading font-medium tracking-tight text-slate-900 leading-snug">
        {s.headline}
      </h3>

      <div className="mt-5 grid grid-cols-3 gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Funded</div>
          <div className="text-base font-medium text-slate-900 mt-0.5">{formatUSD(s.amount)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Commission</div>
          <div className={`text-base font-medium mt-0.5 ${c.accent}`}>{formatUSD(s.commission)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Days</div>
          <div className="text-base font-medium text-slate-900 mt-0.5 inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-slate-400" /> {s.days}
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-3 text-sm text-slate-600 leading-relaxed">
        {s.narrative.map((p, i) => <p key={i}>{p}</p>)}
      </div>

      <div className="mt-6 rounded-xl border-l-4 border-slate-900 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <div className="flex items-start gap-2">
          <TrendingUp className={`h-4 w-4 mt-0.5 shrink-0 ${c.accent}`} />
          <span className="leading-relaxed">{s.outcome}</span>
        </div>
      </div>

      <figure className="mt-auto pt-6">
        <blockquote className="relative pl-6">
          <Quote className={`absolute left-0 top-0 h-4 w-4 ${c.accent} opacity-60`} />
          <p className="text-sm italic text-slate-700 leading-relaxed">&ldquo;{s.quote}&rdquo;</p>
        </blockquote>
        <figcaption className="mt-3 text-xs text-slate-500">— {s.referrer}, {s.referrerRole}</figcaption>
      </figure>
    </article>
  );
};

const Stories = ({ signupPath }) => (
  <section className="bg-slate-50 border-t border-slate-200">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Story examples</p>
        <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
          Three referrals. Three different paychecks.
        </h2>
        <p className="mt-4 text-slate-600 text-lg">
          Here&apos;s what the process actually looks like — from the casual conversation to the wire hitting the account. Names changed; numbers reflect our real commission tiers.
        </p>
      </div>
      <div className="mt-14 grid lg:grid-cols-3 gap-6">
        {STORIES.map((s) => <StoryCard key={s.industry} s={s} signupPath={signupPath} />)}
      </div>
      <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Your story could be next.</h3>
          <p className="text-sm text-slate-600 mt-1">It costs nothing to find out. Submit your first lead whenever you&apos;re ready.</p>
        </div>
        <Link to={signupPath}>
          <Button className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-6">
            Become a Referral Partner <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ───────────── Hero ───────────── */
const Hero = ({ signupPath }) => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-emerald-50/40" />
      <div className="absolute -top-32 -right-24 h-[480px] w-[480px] rounded-full bg-blue-200/40 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-[420px] w-[420px] rounded-full bg-emerald-200/40 blur-3xl" />
    </div>
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 pt-20 md:pt-28 pb-20 md:pb-28">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Now accepting new referral partners
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight text-slate-900 leading-[1.05]">
            Turn Your Network <br className="hidden sm:block" />
            <span className="text-blue-600">Into Income.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
            Refer business owners who need <span className="font-medium text-slate-900">$100K to $1M+</span> in capital and earn a tiered commission when the deal funds. You bring the relationship — we handle underwriting, lender placement, and follow-up.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to={signupPath}>
              <Button
                data-testid={TIDS.heroCtaPrimary}
                size="lg"
                className="rounded-full bg-blue-600 hover:bg-blue-700 px-7 h-12 text-base shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                Become a Referral Partner <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button
                data-testid={TIDS.heroCtaSecondary}
                variant="outline"
                size="lg"
                className="rounded-full px-7 h-12 text-base border-slate-300 hover:bg-slate-50"
              >
                Submit a Referral
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="text-2xl font-heading font-medium text-slate-900">$0</div>
              <div className="text-xs text-slate-500 leading-tight">Cost to join</div>
            </div>
            <div>
              <div className="text-2xl font-heading font-medium text-slate-900">2%</div>
              <div className="text-xs text-slate-500 leading-tight">Top-tier commission</div>
            </div>
            <div>
              <div className="text-2xl font-heading font-medium text-slate-900">100%</div>
              <div className="text-xs text-slate-500 leading-tight">Paid on close</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-200/50 to-emerald-200/30 rounded-3xl blur-xl" />
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-white">
              <img src={HERO_IMG} alt="Business partners discussing funding" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-200 p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Building2 className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-medium text-slate-900">A $1M referral pays</div>
                    <div className="text-xs text-slate-500">$20,000 commission on close.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ───────────── What we fund ───────────── */
const WhatWeFund = () => {
  const items = [
    { title: "Term loans", desc: "Working capital, expansion, payroll bridges — typically $100K–$1M.", Icon: Banknote },
    { title: "Lines of credit", desc: "Flexible revolving capital for established operators.", Icon: LineChart },
    { title: "Equipment financing", desc: "Trucks, machinery, fit-outs — secured against the asset.", Icon: Briefcase },
    { title: "Commercial real-estate", desc: "Acquisition, refi, ground-up — for qualified businesses.", Icon: Building2 },
  ];
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">What we fund</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
              If they need capital — odds are we can place it.
            </h2>
            <p className="mt-5 text-slate-600 text-lg">
              We work with a network of direct lenders, banks, and private capital sources. Most deals close between <span className="font-medium text-slate-900">$100K and $5M</span>. If a business has 12+ months of revenue, it&apos;s worth submitting.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {items.map(({ title, desc, Icon }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-base font-medium text-slate-900">{title}</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────── How it works (detailed) ───────────── */
const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      title: "Join as a referral partner",
      desc: "Sign up in 60 seconds with your name, email, and mobile. SMS-based login means no passwords to remember.",
      Icon: ClipboardList,
      details: ["No license or experience needed", "Free to join", "Get your own dashboard"],
    },
    {
      n: "02",
      title: "Submit a business owner",
      desc: "When you meet someone who needs capital, send us a quick form: business name, owner contact, monthly revenue, and how much they need.",
      Icon: Briefcase,
      details: ["Takes ~2 minutes per referral", "Optional: upload supporting docs", "We follow up with the owner directly"],
    },
    {
      n: "03",
      title: "We run the funding process",
      desc: "Our team handles intake, underwriting, document collection, lender placement, and offer negotiation. You stay in the loop on your dashboard.",
      Icon: FileSearch,
      details: ["Direct lender relationships", "Multiple offers when possible", "Status updates every step"],
    },
    {
      n: "04",
      title: "You get paid on funding",
      desc: "Once the deal funds and clears, your commission is calculated by tier and paid out via your preferred method.",
      Icon: Wallet,
      details: ["Paid only on successful funding", "ACH, wire, check, or PayPal", "Dashboard tracks paid vs unpaid"],
    },
  ];
  return (
    <section className="bg-slate-50 border-y border-slate-200">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">How it works</p>
          <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
            Four steps from intro to payday.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            We make it simple to monetize the relationships you already have. Here&apos;s exactly what happens — from the moment you sign up to the moment the wire hits.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {steps.map(({ n, title, desc, Icon, details }) => (
            <div key={n} className="group relative rounded-2xl border border-slate-200 bg-white p-7 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-400 tracking-widest">STEP {n}</span>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </span>
              </div>
              <h3 className="mt-5 text-lg font-medium text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              <ul className="mt-4 space-y-1.5">
                {details.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────── Commission tiers + live calculator ───────────── */
const CommissionAndCalc = ({ signupPath }) => {
  const [amount, setAmount] = useState(500_000);
  const commission = useMemo(() => computeCommission(amount), [amount]);
  const activeTier = TIERS.findIndex((t) => amount >= t.min && amount < (t.max || Infinity));

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Tier cards */}
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Commission tiers</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
              The bigger the deal,<br /> the bigger your check.
            </h2>
            <p className="mt-5 text-slate-600 text-lg">
              Commissions scale with funded amount. Paid only when the deal closes — no clawbacks, no surprises.
            </p>
            <div className="mt-8 space-y-3">
              {TIERS.map((t, i) => {
                const active = i === activeTier;
                return (
                  <div
                    key={t.label}
                    className={`rounded-2xl border p-5 flex items-center justify-between transition-all ${
                      active
                        ? "border-blue-500 bg-blue-50/60 shadow-md scale-[1.02]"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                        active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        <HandCoins className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="text-sm text-slate-500">Tier {i + 1}</div>
                        <div className="text-base font-medium text-slate-900">{t.label}</div>
                      </div>
                    </div>
                    <div className={`text-2xl font-heading font-medium tracking-tight ${active ? "text-blue-700" : "text-slate-900"}`}>
                      {t.rateLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Calculator */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 to-blue-900 text-white p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur">
                  <Calculator className="h-5 w-5" />
                </span>
                <span className="text-sm uppercase tracking-[0.2em] text-white/70 font-semibold">Live earnings calculator</span>
              </div>
              <p className="mt-6 text-sm text-white/70">If the deal funds at</p>
              <div className="mt-1 text-5xl md:text-6xl font-heading font-medium tracking-tight">
                {formatUSD(amount)}
              </div>
              <p className="mt-2 text-sm text-white/70">you would earn</p>
              <div className="mt-1 text-5xl md:text-6xl font-heading font-medium tracking-tight text-emerald-300">
                {formatUSD(commission)}
              </div>

              <div className="mt-8">
                <Slider
                  value={[amount]}
                  min={100_000}
                  max={5_000_000}
                  step={25_000}
                  onValueChange={(v) => setAmount(v[0])}
                  className="[&_[role=slider]]:bg-emerald-300 [&_[role=slider]]:border-emerald-300"
                />
                <div className="mt-3 flex justify-between text-xs text-white/60 font-mono">
                  <span>$100K</span>
                  <span>$500K</span>
                  <span>$1M</span>
                  <span>$5M</span>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                {[250_000, 500_000, 1_000_000, 2_500_000].map((v) => (
                  <button
                    key={v}
                    onClick={() => setAmount(v)}
                    className={`rounded-full px-4 py-1.5 text-xs font-medium border transition-all ${
                      amount === v
                        ? "bg-emerald-400 text-slate-900 border-emerald-400"
                        : "bg-white/5 text-white/80 border-white/20 hover:bg-white/10"
                    }`}
                  >
                    {formatUSD(v)}
                  </button>
                ))}
              </div>

              <Link to={signupPath} className="mt-10 inline-flex">
                <Button className="rounded-full bg-emerald-400 hover:bg-emerald-300 text-slate-900 font-medium h-11 px-6">
                  Start earning <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────── Who can refer ───────────── */
const WhoRefers = () => {
  const personas = [
    { title: "CPAs & bookkeepers", desc: "You already see who's growing — and who needs working capital." },
    { title: "ISO brokers & advisors", desc: "Add a clean placement channel without the broker tape." },
    { title: "Real estate professionals", desc: "Commercial deals, owner-occupied, business expansions — capital is the unlock." },
    { title: "Payment processors", desc: "Your merchants ask about capital all the time. Now you have an answer." },
    { title: "Business owners", desc: "Your peers, vendors, and customers — they trust you for a reason." },
    { title: "Honestly, anyone connected", desc: "If you know operators, this works for you." },
  ];
  return (
    <section className="bg-slate-50 border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Who refers</p>
          <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
            If you know business owners, this works for you.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Our highest-earning partners come from every walk of life. The common thread: they have trust with operators who occasionally need capital.
          </p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {personas.map(({ title, desc }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Users className="h-4 w-4" /></span>
              <h3 className="mt-4 text-base font-medium text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ───────────── Why us (was Benefits, reframed) ───────────── */
const WhyUs = ({ signupPath }) => {
  const items = [
    { Icon: BadgeCheck, title: "No experience required", desc: "If you know business owners, you're qualified. We train you in minutes." },
    { Icon: ShieldCheck, title: "We handle the heavy lifting", desc: "Underwriting, lender outreach, document collection, follow-up — all on us." },
    { Icon: LineChart, title: "Transparent tracking", desc: "Live dashboard with status, notes from our team, and commission visibility." },
    { Icon: HandCoins, title: "Paid on every funded deal", desc: "No clawbacks. No mystery math. Just clear tiered commissions paid on close." },
  ];
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Why partner with us</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
              Built for relationships.<br /> Designed for trust.
            </h2>
            <p className="mt-5 text-slate-600 text-lg max-w-md">
              We do funding the right way — so you can confidently recommend us to the business owners in your network. No spam, no pressure, no surprises.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={signupPath}>
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                  Start Referring Today <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/partner/login">
                <Button variant="outline" className="rounded-full border-slate-300">Partner Sign-In</Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {items.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-medium text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ───────────── FAQ ───────────── */
const FAQ = () => {
  const items = [
    {
      q: "How much can I earn per referral?",
      a: "Commissions are tiered based on the funded amount: 1% on deals between $100K and $500K, 1.5% on deals between $500K and $1M, and 2% on deals over $1M. A typical $500K funded deal pays $5,000; a $1.5M deal pays $30,000.",
    },
    {
      q: "Do I need a license or financial background?",
      a: "No license is required to refer business owners under this program. You're not underwriting or making any approvals — you're simply introducing the business owner. We do the rest.",
    },
    {
      q: "When and how do I get paid?",
      a: "Commission is paid after the deal funds and clears. You choose your payment method at sign-up — ACH, wire, check, or PayPal. Your dashboard shows a clear 'Commission Paid' status with the exact amount.",
    },
    {
      q: "What kind of businesses qualify?",
      a: "Most operating US businesses with 12+ months of revenue history qualify. Stronger candidates have annual revenue of $1M+, decent bank statements, and a real use case for the capital. We'll let you know quickly if a referral doesn't fit.",
    },
    {
      q: "What happens after I submit a referral?",
      a: "Our team reaches out to the business owner directly within 24 hours, collects documents, and works the deal through underwriting. You'll see status updates on your dashboard at every stage — Contacted, Documents Requested, Submitted to Lender, Offer Received, Funded.",
    },
    {
      q: "Can the business owner know I referred them?",
      a: "That's up to you. You can either submit them yourself or share your unique referral link from your partner dashboard once you're signed up. Either way, you're attributed and earn the commission on close.",
    },
    {
      q: "What if the deal doesn't fund?",
      a: "No commission is owed if the deal doesn't fund. There are no clawbacks, no costs to you, and no risk — referring is genuinely free.",
    },
    {
      q: "Can I track my referrals?",
      a: "Yes — every partner gets their own private dashboard showing every referral, current status, estimated commission, partner-visible notes from our team, and a running tally of paid vs unpaid commissions.",
    },
  ];
  return (
    <section className="bg-slate-50 border-t border-slate-200">
      <div className="mx-auto max-w-5xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">FAQ</p>
        <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
          Questions, answered.
        </h2>
        <Accordion type="single" collapsible className="mt-10">
          {items.map((it, idx) => (
            <AccordionItem
              key={idx}
              value={`item-${idx}`}
              data-testid={`${TIDS.faqItem}-${idx}`}
              className="border-b border-slate-200"
            >
              <AccordionTrigger className="text-left text-base md:text-lg font-medium text-slate-900 hover:text-blue-700">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed text-base">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ───────────── Final CTA ───────────── */
const FinalCTA = ({ signupPath }) => (
  <section className="bg-white">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-24 md:py-28">
      <div className="rounded-3xl overflow-hidden relative bg-gradient-to-br from-blue-600 to-emerald-600 text-white px-8 md:px-16 py-16 md:py-20 shadow-2xl">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 -right-10 h-60 w-60 rounded-full bg-white blur-2xl" />
        </div>
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
            <Sparkles className="h-3.5 w-3.5" /> Limited cohort — onboarding now
          </span>
          <h2 className="mt-5 text-3xl md:text-5xl font-heading font-medium tracking-tight leading-[1.05]">
            Your network is worth more than you think.
          </h2>
          <p className="mt-5 text-lg text-white/85 max-w-xl">
            Sign up free in under two minutes. Submit your first referral when you&apos;re ready. Get paid when it funds.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to={signupPath}>
              <Button size="lg" className="rounded-full bg-white hover:bg-slate-100 text-slate-900 h-12 px-7 font-medium">
                Become a Referral Partner <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent">
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ───────────── Footer ───────────── */
const Footer = ({ signupPath }) => (
  <footer className="bg-slate-900 text-slate-300">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-24 py-20">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white font-bold">BL</span>
            <span className="font-heading font-medium text-white text-lg">becomelenders<span className="text-blue-400">.com</span></span>
          </div>
          <p className="mt-5 text-3xl md:text-4xl font-heading font-medium tracking-tight text-white max-w-md leading-tight">
            Let&apos;s talk.<br />
            <span className="text-slate-400">Your network is worth more than you think.</span>
          </p>
          <Link to={signupPath} className="mt-8 inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-base font-medium">
            Start Referring Today <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="md:col-span-3">
          <h4 className="text-white text-sm font-semibold mb-4">Portal</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to={signupPath} className="hover:text-white transition-colors">Become a Partner</Link></li>
            <li><Link to="/partner/login" className="hover:text-white transition-colors">Partner Login</Link></li>
            <li><Link to="/team/login" data-testid={TIDS.navTeamLogin} className="hover:text-white transition-colors">Team Login</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <h4 className="text-white text-sm font-semibold mb-4">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="tel:+19179452824" className="flex items-center gap-2 hover:text-white transition-colors">
                <PhoneCall className="h-4 w-4 text-emerald-400" /> +1 (917) 945-2824
              </a>
            </li>
            <li>
              <a href="mailto:team@becomelenders.com" className="hover:text-white transition-colors">
                team@becomelenders.com
              </a>
            </li>
            <li className="text-slate-500">Mon – Fri, 9am – 6pm ET</li>
          </ul>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-slate-800 text-xs text-slate-500 leading-relaxed" data-testid={TIDS.footerCompliance}>
        Commissions are paid only on successfully funded deals. Approval and funding are subject to underwriting, lender review, and business qualification. becomelenders.com is not a lender. © {new Date().getFullYear()} becomelenders.com.
      </div>
    </div>
  </footer>
);

/* ───────────── Page ───────────── */
export default function Landing() {
  const location = useLocation();
  const [referrer, setReferrer] = useState(null);
  const refCode = new URLSearchParams(location.search).get("ref");
  const signupPath = refCode ? `/partner/signup?ref=${encodeURIComponent(refCode)}` : "/partner/signup";

  useEffect(() => {
    if (!refCode) return;
    apiClient
      .get(`/partners/lookup/${encodeURIComponent(refCode)}`)
      .then((r) => setReferrer(r.data))
      .catch(() => setReferrer(null));
  }, [refCode]);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {referrer ? (
        <div
          data-testid={TIDS.referralBanner}
          className="bg-emerald-600 text-white text-center text-sm py-2 px-6"
        >
          You were referred by <span className="font-semibold">{referrer.full_name}</span>
          {referrer.company_name ? ` (${referrer.company_name})` : ""} — welcome!
        </div>
      ) : null}

      <Hero signupPath={signupPath} />
      <WhatWeFund />
      <HowItWorks />
      <Stories signupPath={signupPath} />
      <CommissionAndCalc signupPath={signupPath} />
      <WhoRefers />
      <WhyUs signupPath={signupPath} />
      <FAQ />
      <FinalCTA signupPath={signupPath} />
      <Footer signupPath={signupPath} />
    </div>
  );
}
